import type { FormikValues } from 'formik';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import Auth from '../../../api/auth';
import { FormModal } from '../../../components/Formik';
import { regexValidator } from '../../../helpers/regexValidator';
import useApiQuery from '../../../hooks/useApiQuery';

import type { APIHookArg } from '@/types';
import type { AccountOwner, Organization } from '@/types/organization';

interface EditOrganizationFormModalProps {
  organization: Organization;
  onSubmit: (data: Organization) => void;
  onCancel: () => void;
}

const EditOrganizationFormModal: React.FC<EditOrganizationFormModalProps> = ({
  organization,
  onSubmit,
  onCancel,
}) => {
  const { organizationId } = useParams<{ organizationId: string }>();

  const { data: accountOwnersData, makeApiCall: getAccountOwners } = useApiQuery<AccountOwner[]>(
    Auth.fetchV2Users
  );
  const { makeApiCall: updateOrganizationCall } = useApiQuery(
    Auth.updateV1Organization as unknown as APIHookArg,
    {
      successMessage: 'organizationUpdated',
    }
  );

  const accountOwners = accountOwnersData?.filter(
    (accountOwner: AccountOwner) => accountOwner.activated
  );

  useEffect(() => {
    getAccountOwners({
      organizationId: organization._id,
      roles: ['ACCOUNT_OWNER'],
    });
  }, [getAccountOwners, organization._id]);

  const fields = [
    {
      fieldType: 'text',
      name: 'enterpriseName',
      label: 'Name',
    },
    {
      fieldType: 'text',
      name: 'CRMId',
      label: 'CRM Id',
    },
    ...(accountOwners
      ? [
          {
            fieldType: 'select',
            name: 'accountOwnerId',
            label: 'Account Owner',
            menuItems: accountOwners.map((accountOwner: AccountOwner) => ({
              value: accountOwner._id,
              label: accountOwner.email,
            })),
          },
        ]
      : []),
  ];

  const initialValues: FormikValues = {
    enterpriseName: organization.enterpriseName,
    CRMId: organization.CRMId,
    accountOwnerId: organization.accountOwnerId ?? '',
  };

  const validationSchema = Yup.object().shape({
    enterpriseName: Yup.string()
      .required('Required')
      .matches(
        regexValidator.name,
        'Start with letter, all lower-case, no spaces, no special characters(dash only)'
      ),
  });

  const updateOrganization = async (
    formValues: FormikValues & { accountOwnerId?: string | null }
  ): Promise<void> => {
    onCancel();

    const data = await updateOrganizationCall({
      ...formValues,
      accountOwnerId: formValues.accountOwnerId ?? null,
      _id: organizationId,
    });

    if (data) {
      onSubmit(data as Organization);
    }
  };

  return (
    <FormModal
      title={`Edit organization ${organization.enterpriseName}`}
      fields={fields}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => {
        updateOrganization(values);
      }}
      onCancel={onCancel}
      submitLabel='Save'
    />
  );
};

export default EditOrganizationFormModal;
